/* MERIS global utilities — namespaced, defensive, Shopify editor-safe */
(function () {
  'use strict';

  /* ---------- Reveal on scroll ---------- */
  var revealObserver = null;
  function initReveals(root) {
    var els = (root || document).querySelectorAll('.meris-reveal:not(.is-visible)');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    }
    els.forEach(function (el) { revealObserver.observe(el); });
  }

  /* Deferred scripts run with the DOM ready: init immediately, plus fallbacks. */
  initReveals(document);
  document.addEventListener('DOMContentLoaded', function () { initReveals(document); });
  document.addEventListener('shopify:section:load', function (e) { initReveals(e.target); });
  /* Safety net: nothing may ever stay hidden. */
  setTimeout(function () {
    document.querySelectorAll('.meris-reveal:not(.is-visible)').forEach(function (el) {
      el.classList.add('is-visible');
    });
  }, 1800);

  /* ---------- Accessible tab group <meris-tabs> ----------
     Markup contract:
     <meris-tabs>
       [role=tablist] > .meris-tab[role=tab][aria-controls=panelId]
       panels: [role=tabpanel][id=panelId] (hidden except active)
     </meris-tabs> */
  if (!customElements.get('meris-tabs')) {
    customElements.define('meris-tabs', class extends HTMLElement {
      connectedCallback() {
        this._onClick = this._handleClick.bind(this);
        this._onKey = this._handleKey.bind(this);
        this.addEventListener('click', this._onClick);
        this.addEventListener('keydown', this._onKey);
      }
      disconnectedCallback() {
        this.removeEventListener('click', this._onClick);
        this.removeEventListener('keydown', this._onKey);
      }
      get tabs() {
        return Array.prototype.slice.call(this.querySelectorAll('[role="tab"]'));
      }
      _handleClick(e) {
        var tab = e.target.closest('[role="tab"]');
        if (tab && this.contains(tab)) this.activate(tab);
      }
      _handleKey(e) {
        var tab = e.target.closest('[role="tab"]');
        if (!tab) return;
        var tabs = this.tabs;
        var i = tabs.indexOf(tab);
        var next = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = tabs[(i + 1) % tabs.length];
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = tabs[(i - 1 + tabs.length) % tabs.length];
        if (e.key === 'Home') next = tabs[0];
        if (e.key === 'End') next = tabs[tabs.length - 1];
        if (next) {
          e.preventDefault();
          next.focus();
          this.activate(next);
        }
      }
      activate(tab) {
        var self = this;
        this.tabs.forEach(function (t) {
          var selected = t === tab;
          t.setAttribute('aria-selected', selected ? 'true' : 'false');
          t.tabIndex = selected ? 0 : -1;
          var panel = t.getAttribute('aria-controls') && self.querySelector('#' + CSS.escape(t.getAttribute('aria-controls')));
          if (!panel) return;
          if (selected) {
            panel.hidden = false;
            requestAnimationFrame(function () { panel.classList.remove('is-leaving'); });
          } else {
            panel.classList.add('is-leaving');
            panel.hidden = true;
          }
        });
        this.dispatchEvent(new CustomEvent('meris:tab-change', { detail: { tab: tab }, bubbles: true }));
      }
    });
  }

  /* ---------- Simple rail controls <meris-rail-nav> ---------- */
  if (!customElements.get('meris-rail-nav')) {
    customElements.define('meris-rail-nav', class extends HTMLElement {
      connectedCallback() {
        this._onClick = this._handleClick.bind(this);
        this.addEventListener('click', this._onClick);
      }
      disconnectedCallback() {
        this.removeEventListener('click', this._onClick);
      }
      _handleClick(e) {
        var btn = e.target.closest('[data-rail-dir]');
        if (!btn) return;
        var railId = this.getAttribute('for');
        var rail = railId ? document.getElementById(railId) : this.parentElement.querySelector('.meris-rail');
        if (!rail) return;
        var card = rail.firstElementChild;
        var step = card ? card.getBoundingClientRect().width + 14 : rail.clientWidth * 0.8;
        rail.scrollBy({ left: btn.dataset.railDir === 'next' ? step : -step, behavior: 'smooth' });
      }
    });
  }

  /* ---------- Video lazy + single-audio guard <meris-video-card> ---------- */
  if (!customElements.get('meris-video-card')) {
    customElements.define('meris-video-card', class extends HTMLElement {
      connectedCallback() {
        var video = this.querySelector('video');
        if (!video) return;
        this._video = video;
        this._io = new IntersectionObserver(this._onIntersect.bind(this), { threshold: 0.35 });
        this._io.observe(this);
        this._onPlay = this._pauseOthers.bind(this);
        video.addEventListener('play', this._onPlay);
      }
      disconnectedCallback() {
        if (this._io) this._io.disconnect();
        if (this._video) this._video.removeEventListener('play', this._onPlay);
      }
      _onIntersect(entries) {
        var video = this._video;
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            if (video.dataset.src && !video.src) video.src = video.dataset.src;
            var host = video.closest('meris-video-card');
            var wantsAutoplay = video.dataset.autoplay === 'true' || (host && host.dataset.autoplayVideos === 'true');
            if (wantsAutoplay && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
              video.muted = true;
              var p = video.play();
              if (p && p.catch) p.catch(function () {});
            }
          } else if (!video.paused) {
            video.pause();
          }
        });
      }
      _pauseOthers() {
        var current = this._video;
        if (current.muted) return;
        document.querySelectorAll('meris-video-card video').forEach(function (v) {
          if (v !== current && !v.paused && !v.muted) v.pause();
        });
      }
    });
  }

  /* ---------- Autoplaying rail wrapper <meris-carousel> ---------- */
  if (!customElements.get('meris-carousel')) {
    customElements.define('meris-carousel', class extends HTMLElement {
      connectedCallback() {
        if (this.dataset.autoplay !== 'true') return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        this._rail = this.querySelector('.meris-rail');
        if (!this._rail) return;
        this._interval = parseInt(this.dataset.interval, 10) || 5000;
        this._paused = false;
        this._onEnter = () => { this._paused = true; };
        this._onLeave = () => { this._paused = false; };
        this.addEventListener('mouseenter', this._onEnter);
        this.addEventListener('mouseleave', this._onLeave);
        this.addEventListener('focusin', this._onEnter);
        this.addEventListener('focusout', this._onLeave);
        this._timer = setInterval(() => {
          if (this._paused || !this._rail.isConnected) return;
          var card = this._rail.firstElementChild;
          if (!card) return;
          var step = card.getBoundingClientRect().width + 14;
          var atEnd = this._rail.scrollLeft + this._rail.clientWidth >= this._rail.scrollWidth - 4;
          if (atEnd) {
            this._rail.scrollTo({ left: 0, behavior: 'smooth' });
          } else {
            this._rail.scrollBy({ left: step, behavior: 'smooth' });
          }
        }, this._interval);
      }
      disconnectedCallback() {
        if (this._timer) clearInterval(this._timer);
        this.removeEventListener('mouseenter', this._onEnter);
        this.removeEventListener('mouseleave', this._onLeave);
        this.removeEventListener('focusin', this._onEnter);
        this.removeEventListener('focusout', this._onLeave);
      }
    });
  }
})();
